import React, { useEffect, useState, useCallback } from 'react';

function PmsPettyCash({ token, selectedProperty }) {
  const [shift, setShift] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [opening, setOpening] = useState('');
  const [refillAmt, setRefillAmt] = useState('');
  const [refillRemark, setRefillRemark] = useState('');
  const [closingActual, setClosingActual] = useState('');
  const [closingNotes, setClosingNotes] = useState('');

  const headers = (extra = {}) => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
    ...extra
  });

  const loadCurrent = useCallback(async () => {
    if (!selectedProperty?.id) return;
    try {
      const res = await fetch('/api/v1/expense/petty-cash/current', { headers: headers() });
      const data = await res.json();
      if (!res.ok) return setError(data.error || 'Failed to load');
      setShift(data.shift || null);
      setError('');
    } catch { setError('Network error'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedProperty?.id]);

  useEffect(() => { loadCurrent(); }, [loadCurrent]);

  const openShift = async () => {
    if (!opening || Number(opening) < 0) return setError('Opening balance required');
    setError('');
    setMessage('');
    try {
      const res = await fetch('/api/v1/expense/petty-cash/open', {
        method: 'POST',
        headers: headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ opening_balance: Number(opening) })
      });
      const data = await res.json();
      if (!res.ok) return setError(data.error || 'Open failed');
      setMessage('Shift opened.');
      setOpening('');
      loadCurrent();
    } catch { setError('Network error'); }
  };

  const refill = async () => {
    if (!shift || !refillAmt) return setError('Refill amount required');
    setError('');
    setMessage('');
    try {
      const res = await fetch('/api/v1/expense/petty-cash/refill', {
        method: 'POST',
        headers: headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ shift_id: shift.shift_id, amount: Number(refillAmt), remarks: refillRemark })
      });
      const data = await res.json();
      if (!res.ok) return setError(data.error || 'Refill failed');
      setMessage(`Refilled. New balance: ₹ ${Number(data.new_balance).toLocaleString('en-IN')}`);
      setRefillAmt('');
      setRefillRemark('');
      loadCurrent();
    } catch { setError('Network error'); }
  };

  const close = async () => {
    if (!shift || closingActual === '') return setError('Closing actual required');
    if (!window.confirm('Close this shift? Cannot be reopened.')) return;
    setError('');
    setMessage('');
    try {
      const res = await fetch('/api/v1/expense/petty-cash/close', {
        method: 'POST',
        headers: headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ shift_id: shift.shift_id, closing_actual: Number(closingActual), notes: closingNotes })
      });
      const data = await res.json();
      if (!res.ok) return setError(data.error || 'Close failed');
      setMessage(`Shift closed. Expected: ₹ ${Number(data.closing_expected).toLocaleString('en-IN')} · Variance: ₹ ${Number(data.variance).toLocaleString('en-IN')}`);
      setClosingActual('');
      setClosingNotes('');
      loadCurrent();
    } catch { setError('Network error'); }
  };

  const fmtMoney = (n) => `₹ ${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const inputStyle = { padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.9rem' };

  return (
    <div className="page-shell">
      <h2 style={{ color: 'var(--theme-primary-dark)', marginBottom: '0.8rem' }}>Petty Cash</h2>
      {!selectedProperty?.id && <div className="error-banner">Select a property first.</div>}
      {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '0.6rem 1rem', borderRadius: 6, marginBottom: '0.8rem' }}>{error}</div>}
      {message && <div style={{ background: '#dcfce7', color: '#166534', padding: '0.6rem 1rem', borderRadius: 6, marginBottom: '0.8rem' }}>{message}</div>}

      {!shift ? (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '1.2rem' }}>
          <h3 style={{ margin: '0 0 0.7rem 0' }}>Open New Shift</h3>
          <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>You don't have an open petty-cash shift. Open one to start recording cash payouts.</p>
          <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-end', maxWidth: 420 }}>
            <label style={{ display: 'grid', gap: '0.25rem', fontSize: '0.85rem', flex: 1 }}>
              <span>Opening Balance</span>
              <input type="number" step="0.01" value={opening} onChange={e => setOpening(e.target.value)} style={inputStyle} />
            </label>
            <button onClick={openShift} style={{ padding: '0.55rem 1.2rem', border: 'none', borderRadius: 6, background: '#2563eb', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>Open Shift</button>
          </div>
        </div>
      ) : (
        <>
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '1rem', marginBottom: '0.8rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.7rem', fontSize: '0.9rem' }}>
              <div><div style={{ color: '#6b7280', fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase' }}>Shift Date</div><div style={{ fontWeight: 600 }}>{shift.shift_date}</div></div>
              <div><div style={{ color: '#6b7280', fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase' }}>Opening</div><div style={{ fontWeight: 600 }}>{fmtMoney(shift.opening_balance)}</div></div>
              <div><div style={{ color: '#6b7280', fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase' }}>Status</div><div style={{ fontWeight: 600, color: '#16a34a' }}>{shift.status}</div></div>
            </div>
          </div>

          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '1rem', marginBottom: '0.8rem' }}>
            <h3 style={{ margin: '0 0 0.7rem 0', fontSize: '1rem' }}>Refill</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.7rem' }}>
              <label style={{ display: 'grid', gap: '0.25rem', fontSize: '0.85rem' }}><span>Amount</span><input type="number" step="0.01" value={refillAmt} onChange={e => setRefillAmt(e.target.value)} style={inputStyle} /></label>
              <label style={{ display: 'grid', gap: '0.25rem', fontSize: '0.85rem' }}><span>Remarks</span><input value={refillRemark} onChange={e => setRefillRemark(e.target.value)} style={inputStyle} /></label>
              <div style={{ alignSelf: 'end' }}><button onClick={refill} style={{ padding: '0.5rem 1rem', border: '1px solid #0ea5e9', borderRadius: 6, background: '#0ea5e9', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>Record Refill</button></div>
            </div>
          </div>

          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '1rem' }}>
            <h3 style={{ margin: '0 0 0.7rem 0', fontSize: '1rem' }}>Close Shift</h3>
            <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>Count the cash on hand and enter actual closing balance. Variance will be auto-computed.</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.7rem' }}>
              <label style={{ display: 'grid', gap: '0.25rem', fontSize: '0.85rem' }}><span>Closing Actual</span><input type="number" step="0.01" value={closingActual} onChange={e => setClosingActual(e.target.value)} style={inputStyle} /></label>
              <label style={{ display: 'grid', gap: '0.25rem', fontSize: '0.85rem' }}><span>Notes</span><input value={closingNotes} onChange={e => setClosingNotes(e.target.value)} style={inputStyle} /></label>
              <div style={{ alignSelf: 'end' }}><button onClick={close} style={{ padding: '0.5rem 1rem', border: 'none', borderRadius: 6, background: '#dc2626', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>Close Shift</button></div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default PmsPettyCash;
